// FA's user id is the `sub` claim inside the id_token JWT. NextAuth's
// `session.user.id` is a NextAuth-internal subject and does NOT match the FA
// user id — see project memory [[feedback-nextauth-session-shape]]. Anything
// that wants to identify the FA user (Secutix sync, analytics user
// properties, server routes that call FA's API) must decode the id_token.

// Permissive shape so callers can pass a next-auth `Session` (whose
// augmented `user` has name/email/image but not necessarily id/sub)
// without casting. We treat anything on `user` as optional and only
// inspect the fields we care about; runtime type-narrowing inside the
// function handles the rest.
type SessionLike =
  | {
      id_token?: string | null;
      user?: unknown;
    }
  | null
  | undefined;

export function decodeJwtSub(
  jwt: string | null | undefined
): string | undefined {
  if (!jwt) return undefined;
  const parts = jwt.split('.');
  if (parts.length < 2 || !parts[1]) return undefined;
  try {
    // Uses `atob` not Node's Buffer so this works in client + server contexts.
    // JWT uses base64url (- / _ instead of + /) and omits padding; convert
    // and pad before atob.
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.sub === 'string' ? payload.sub : undefined;
  } catch {
    return undefined;
  }
}

// Falls through `session.user.id` -> `session.user.sub` -> `decodeJwtSub(id_token)`.
// In atom today the first two are NextAuth-internal subjects, so the id_token
// decode is what produces the real FA user id; the earlier branches are kept
// as a forward-compat hatch in case the session shape ever exposes the FA
// sub directly on `user`.
export function extractFaUserId(session: SessionLike): string | undefined {
  if (!session) return undefined;
  const user = session.user as
    | { id?: string | null; sub?: string | null }
    | null
    | undefined;
  if (user?.id) return user.id;
  if (user?.sub) return user.sub;
  return decodeJwtSub(session.id_token);
}
